/**
 * AuthClient — the auth domain (/api/v1/auth/*) as the static Apps
 * consume it. Endpoint paths, wire types and semantics are locked in
 * @web-v4/api-contract/v1/auth; this class only composes the transport
 * (@web-v4/api-client) with the CSRF lifecycle (@web-v4/csrf-client):
 *
 *   - every write goes through csrf.withRefreshRetry (refresh once,
 *     retry once on 419 csrf_failed);
 *   - login/logout change the session binding of the CSRF token, so a
 *     fresh bootstrap follows them automatically (best effort — see
 *     `rebindCsrf` below);
 *   - NO session state is cached here: GET /auth/me is the only oracle
 *     (roles are resolved live server-side; apps that need state keep it
 *     in their own store and re-call me() after role-changing actions).
 *
 * All methods resolve the envelope's `data` payload and throw the
 * api-client error classes — form UIs read `ApiError.fields` for
 * per-field messages and branch on `ApiError.code` only.
 */
import { CAPTCHA_CHALLENGE_ENDPOINT, LOGIN_ENDPOINT, LOGOUT_ENDPOINT, ME_ENDPOINT, PASSWORD_FORGOT_ENDPOINT, PASSWORD_RESET_ENDPOINT, REGISTER_ENDPOINT, REGISTER_NAME_CHECK_ENDPOINT, REGISTER_SKIN_CHECK_ENDPOINT, REGISTER_STAMPED_SKIN_ENDPOINT, REGISTER_VERIFICATION_EMAIL_ENDPOINT, REGISTER_VERIFICATION_ENDPOINT, } from '@web-v4/api-contract/v1/auth';
import { ApiError } from '@web-v4/api-client';
export class AuthClient {
    api;
    csrf;
    rebindCsrf;
    constructor(options) {
        this.api = options.api;
        this.csrf = options.csrf;
        this.rebindCsrf = options.rebindCsrf ?? true;
    }
    /**
     * GET /api/v1/auth/me — the session oracle.
     * Resolves MeData when authenticated, or null on 401 unauthorized
     * (anonymous / expired / revoked session). Every other error throws.
     */
    async me() {
        try {
            const envelope = await this.api.request(ME_ENDPOINT);
            return envelope.data;
        }
        catch (error) {
            if (error instanceof ApiError && error.is('unauthorized')) {
                return null;
            }
            throw error;
        }
    }
    /**
     * POST /api/v1/auth/login — password login (username + password +
     * captcha answer). Resolves on 200 (session cookie set by the server);
     * throws ApiError for validation_failed / captcha_failed /
     * too_many_requests — the fields bag carries the user-visible copy.
     * A fresh CSRF bootstrap follows a successful login.
     */
    async login(body) {
        const data = await this.write(LOGIN_ENDPOINT, body);
        await this.afterSessionChange();
        return data;
    }
    /**
     * POST /api/v1/auth/logout — revokes the current session. A 401
     * unauthorized (no live session) is success-equivalent per the
     * contract and resolves normally. A fresh CSRF bootstrap follows.
     */
    async logout() {
        try {
            await this.write(LOGOUT_ENDPOINT);
        }
        catch (error) {
            if (!(error instanceof ApiError && error.is('unauthorized'))) {
                throw error;
            }
        }
        await this.afterSessionChange();
    }
    /**
     * GET /api/v1/auth/captcha — one challenge per captcha-gated form
     * render; burned by every verify attempt, so refetch after any
     * captcha_failed.
     */
    async captcha() {
        const envelope = await this.api.request(CAPTCHA_CHALLENGE_ENDPOINT);
        return envelope.data;
    }
    /**
     * Registration step 1 — POST /api/v1/auth/register/verification-email.
     * Always resolves `sent: true` on 200 (mailbox existence is never
     * confirmed); 429 carries the throttle wait message in fields.email.
     */
    async requestVerificationEmail(body) {
        return this.write(REGISTER_VERIFICATION_EMAIL_ENDPOINT, body);
    }
    /**
     * Registration step 2 — GET /api/v1/auth/register/verification?u=&h=.
     * Resolves the continuation payload the step-3 form echoes back.
     * Safe-method GET: no CSRF involved. 400 registration_link_invalid
     * means restart from step 1.
     */
    async resolveVerification(query) {
        // Spread: QueryParams is a Record, and interfaces have no implicit
        // index signature — the spread re-widens to an anonymous object type.
        const envelope = await this.api.request(REGISTER_VERIFICATION_ENDPOINT, {
            query: { ...query },
        });
        return envelope.data;
    }
    /**
     * Registration step 3 — POST /api/v1/auth/register. Resolves on 201.
     * 201 同时 Set-Cookie 建立会话（auto-login），会话绑定从匿名翻转为
     * s:<session>——与 login/logout 一样，成功后立即 rebind CSRF，否则
     * 注册后该 tab 的第一次写请求必带旧匿名 token 撞 419。
     */
    async register(body) {
        const data = await this.write(REGISTER_ENDPOINT, body);
        await this.afterSessionChange();
        return data;
    }
    /**
     * Registration step 2c — GET /api/v1/auth/register/skin-check?u=&h=&username=.
     * 只读探测玩家是否已把盖章皮肤上传到 minecraft.net;verified:false
     * 是正常业务结果(不是错误),玩家可重试。Safe-method GET: no CSRF.
     */
    async checkRegisterSkin(query) {
        const envelope = await this.api.request(REGISTER_SKIN_CHECK_ENDPOINT, {
            query: { ...query },
        });
        return envelope.data;
    }
    /**
     * Registration step 2d — GET /api/v1/auth/register/name-check?u=&h=&username=.
     * 普通注册侧的昵称只读预检,与 checkRegisterSkin 镜像但语义相反:
     * 200 available:true 即「未被占用且不属于正版玩家」;已占用/正版名
     * 抛 validation_failed(fields.username 带文案),Mojang 故障 502。
     * Safe-method GET: no CSRF.
     */
    async checkRegisterName(query) {
        const envelope = await this.api.request(REGISTER_NAME_CHECK_ENDPOINT, {
            query: { ...query },
        });
        return envelope.data;
    }
    /**
     * Password reset step 1 — POST /api/v1/auth/password/forgot.
     * Resolves on 200 (mail queued); throws ApiError validation_failed
     * with fields.email「该邮箱未注册」for unknown mailboxes (legacy
     * enumerable behavior), captcha_failed (refetch challenge), or
     * too_many_requests.
     */
    async requestPasswordReset(body) {
        return this.write(PASSWORD_FORGOT_ENDPOINT, body);
    }
    /**
     * Password reset step 2 — POST /api/v1/auth/password/reset.
     * Resolves on 200 (「密码重置成功」); throws ApiError
     * validation_failed with fields.token「无效的密码重置链接」for
     * expired/unknown tokens.
     */
    async confirmPasswordReset(body) {
        return this.write(PASSWORD_RESET_ENDPOINT, body);
    }
    /** One CSRF-protected write: headers from the lifecycle, 419 dance included. */
    async write(endpoint, body) {
        const envelope = await this.csrf.withRefreshRetry((headers) => this.api.request(endpoint, body === undefined ? { headers } : { body, headers }));
        return envelope.data;
    }
    /** Best-effort CSRF re-binding after a session change (see AuthClientOptions.rebindCsrf). */
    async afterSessionChange() {
        if (!this.rebindCsrf) {
            return;
        }
        try {
            await this.csrf.refresh();
        }
        catch {
            // Best effort by design: the session change itself already
            // succeeded server-side, and the next write's withRefreshRetry
            // re-heals the token on a 419. Surfacing the bootstrap failure
            // here would misreport a successful login/logout as failed.
        }
    }
}
/** 构造盖章皮肤下载 URL（GET, image/png;浏览器直接导航下载）。 */
export function stampedSkinUrl(query) {
    const params = new URLSearchParams(query);
    return `${REGISTER_STAMPED_SKIN_ENDPOINT.path}?${params.toString()}`;
}
//# sourceMappingURL=client.js.map